import React, { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { Link as ScrollLink, animateScroll as scroll } from "react-scroll";
import { Helmet } from "react-helmet";
import { Link, withPrefix } from "gatsby";
import Lightbox from "react-image-lightbox";
import ReactJWPlayer from "react-jw-player";
import favicon from '../images/favicon.ico';
import eieuLogo from '../images/eieu-logo.png';
import lttcLogo from '../images/lttc-logo.png';
import spfeoLogo from '../images/spfeo-logo.png';
import eieuSLogo from '../images/eieu-s-logo.png';
import lttcSLogo from '../images/lttc-s-logo.png';
import spfeoSLogo from '../images/spfeo-s-logo.png';
import eduhkBwLogo from '../images/eduhk-logo-60.png';
import libBwLogo from '../images/eduhklib-logo-60.png';
import eieuBwLogo from '../images/eieu-logo-60.png';
import lttcBwLogo from '../images/lttc-bw-logo.png';
import spfeoBwLogo from '../images/spfeo-bw-logo.png';
import aboutImage from '../images/about.png';
import appStudents from '../images/app-students.png';
import appSchools from '../images/app-schools.png';
import panelImage from '../images/panel.png';
import awardsImage from '../images/awards.png';

export default function Home() {
  const aboutRef = useRef(null);
  const [showRegulations, setShowRegulations] = useState(false);
  const [scrollVisible, setScrollVisible] = useState(false);
  const totalSnapshots = 76;
  const [snapshotIndex, setSnapshotIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    function check() {
      if (aboutRef && aboutRef.current) {
        setScrollVisible(window.pageYOffset > aboutRef.current.offsetTop);
      }
    }
    window.addEventListener("scroll", check);
    return () => {
      window.removeEventListener("scroll", check);
    };
  }, []);

  const snapshotClicked = (index) => {
    setSnapshotIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div>
      <Helmet>
        <title>“Think & Design” Product Design Competition</title>
        <link rel="icon" href={favicon} />
      </Helmet>
      <header>
        <div className="container mx-auto px-2 py-2 lg:px-0 lg:py-0">
          <div className="text-right pt-2 lg:block hidden">
            <Link to="/tc">中文</Link>
          </div>
          <div className="flex justify-between items-center lg:pb-4 my-1">
            <div className="lg:flex lg:items-center hidden">
              <div className="mr-4">
                <img src={eieuLogo} width="120" height="60" alt="EIEU" />
              </div>
              <div className="mr-4">
                <img src={lttcLogo} width="160" height="60" alt="LTTC" />
              </div>
              <div>
                <img src={spfeoLogo} width="160" height="60" alt="SPFEO" />
              </div>
            </div>
            <div className="lg:hidden items-center flex">
              <div className="mr-4">
                <img src={eieuSLogo} width="60" height="30" alt="EIEU" />
              </div>
              <div className="mr-4">
                <img src={lttcSLogo} width="30" height="30" alt="LTTC" />
              </div>
              <div>
                <img src={spfeoSLogo} width="30" height="30" alt="SPFEO" />
              </div>
            </div>
            <div className="lg:hidden">
              <Link to="/tc">中文</Link>
            </div>
            <div className="lg:flex hidden">
              <ScrollLink
                to="about"
                smooth={true}
                duration={500}
                className="cursor-pointer hover:text-orange-600 font-semibold text-lg ml-8"
              >
                About
              </ScrollLink>
              <ScrollLink
                to="schedule"
                smooth={true}
                duration={500}
                className="cursor-pointer hover:text-orange-600 font-semibold text-lg ml-8"
              >
                Schedule
              </ScrollLink>
              <ScrollLink
                to="adjudication"
                smooth={true}
                duration={500}
                className="cursor-pointer hover:text-orange-600 font-semibold text-lg ml-8"
              >
                Adjudication
              </ScrollLink>
              <ScrollLink
                to="results"
                smooth={true}
                duration={500}
                className="cursor-pointer hover:text-orange-600 font-semibold text-lg ml-8"
              >
                Results
              </ScrollLink>
            </div>
          </div>
        </div>
        <div className="lg:h-96 h-72 flex items-center bg-container">
          <div className="container mx-auto">
            <div className="flex items-center flex-col">
              <h1 className="lg:text-6xl text-4xl font-bold text-center">
                “Think &amp; Design”
              </h1>
              <h1 className="lg:text-4xl text-2xl text-center mt-3">
                Product Design Competition
              </h1>
              <h3 className="lg:text-2xl text-lg text-center text-blue-theme mt-3">
                EdUHK + schools students, think collaboratively, serving the
                community
              </h3>
              <ScrollLink
                to="results"
                smooth={true}
                duration={500}
                className="cta cursor-pointer mt-5"
              >
                Check Final Results
              </ScrollLink>
            </div>
          </div>
        </div>
      </header>

      <main>
        <div ref={aboutRef} id="about" className="lg:py-20 py-10">
          <div className="container mx-auto px-4 lg:px-0">
            <div className="lg:flex">
              <div className="lg:flex-1 lg:pr-10">
                <h5 className="text-orange-600">#about</h5>
                <h3 className="lg:text-3xl text-xl font-bold">
                  “Think &amp; Design” Product Design Competition
                </h3>
                <p className="leading-loose mt-3">
                  “Think & Design” is a product design competition with an
                  objective to nourish innovative and entrepreneurial skills of
                  Hong Kong students and to inspire them using STEM skills by
                  arranging EdUHK students to lead student teams from primary
                  and secondary schools on designing and prototyping physical
                  products or digital solutions on the theme “
                  <b>Improving Daily Life of a Target Group</b>” specified by
                  the Organiser. This event will adopt a TTT (Train The Trainer)
                  approach to train EdUHK students to become leaders of
                  individual School Teams. “Think & Design” Product Design
                  Competition is jointly organized by Entrepreneurship and
                  Innovation Education Unit of Library, Centre for Learning,
                  Teaching and Technology, and School Partnership and Field
                  Experience Office of EdUHK, and is funded by the University
                  Grants Committee (UGC).
                </p>
                <ScrollLink
                  to="regulations"
                  smooth={true}
                  duration={500}
                  className="inline-block cta mt-5 cursor-pointer"
                >
                  Read Regulations
                </ScrollLink>
              </div>
              <div className="lg:w-1/3">
                <div className="lg:mt-0 mt-4">
                  <img src={aboutImage} width="500" height="300" alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="schedule" className="lg:py-20 py-10 bg-gray-50 relative">
          <div className="lg:block hidden lg:opacity-50 absolute top-0 right-0 top-1/2 -mt-24 mr-14">
            <svg
              className="w-48 h-48 text-gray-300 transform rotate-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              ></path>
            </svg>
          </div>
          <div className="container mx-auto px-4 lg:px-0">
            <h5 className="text-orange-600">#schedule</h5>
            <h3 className="lg:text-3xl text-xl font-bold">
              Schedule &amp; Timeline
            </h3>
            <div className="lg:mt-10 mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    12 Jul 2021
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    Briefing for EdUHK Students
                  </h4>
                  <p className="text-gray-500 mt-1">
                    <a
                      href="https://edvideo.lib.eduhk.hk/archive/eieu/think-briefing-session-zoom/"
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Click here
                    </a>{" "}
                    to watch a briefing session for EdUHK students via Zoom.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    9 Aug 2021
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    Application Deadline for EdUHK Students
                  </h4>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    Aug 2021
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    Training Workshops for EdUHK Students
                  </h4>
                  <p className="text-gray-500 mt-1">
                    Workshops on product design and prototyping as well as
                    mobile apps development will be provided for EdUHK students.{" "}
                    <Link
                      to="/training-for-students"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Click here
                    </Link>{" "}
                    for workshop contents.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    23 Aug 2021
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    Briefing for Interested Schools
                  </h4>
                  <p className="text-gray-500 mt-1">
                    <a
                      href="https://edvideo.lib.eduhk.hk/archive/eieu/think-briefing-schools"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Briefing session video
                    </a>
                  </p>
                  <p className="text-gray-500">
                    <a
                      href="/Briefing-for-schools-2021-08-23-rev-08-25.pptx"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Briefing session PowerPoint (Chinese version only)
                    </a>
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    6 Sep 2021
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    Application Deadline for Interested Schools
                  </h4>
                  <p className="text-gray-500 mt-1">
                    Local primary and secondary schools invited by SPFEO are
                    required to{" "}
                    <a
                      href="https://edulib.me/think-sch"
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      submit their application online
                    </a>{" "}
                    by this date.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    7 Sep 2021
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    Briefing for EdUHK Student Team Leaders
                  </h4>
                  <p className="text-gray-500 mt-1">
                    <p className="text-gray-500 mt-1">
                      <a
                        href="https://edvideo.lib.eduhk.hk/archive/eieu/think-briefing-team-leaders"
                        target="_blank"
                        className="text-blue-600 hover:text-blue-700 underline"
                      >
                        Briefing session video
                      </a>
                    </p>
                    <p className="text-gray-500">
                      <a
                        href="/Think-Design-Team-Leaders-Guideline-2021-09-07.docx.pdf"
                        target="_blank"
                        className="text-blue-600 hover:text-blue-700 underline"
                      >
                        Briefing session guideline
                      </a>
                    </p>
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    11 Sep 2021
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    Tour for Interested Schools
                  </h4>
                  <p className="text-gray-500 mt-1">
                    Tour of Future Classrooms and EI Hub to be organised for
                    interested schools.
                  </p>
                  <p className="text-red-500">
                    Bad weather arrangement: The tour will be postponed for one
                    week to 18 Sep morning if T3/Red Rainstorm warning signal is
                    in force at or after 7:30 am on 11 Sep 2021.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    13 Sep 2021
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    Assignment of Team Leader
                  </h4>
                  <p className="text-gray-500 mt-1">
                    Each accepted EdUHK student to be randomly assigned as team
                    leader of one accepted school team.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    26 Nov 2021
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    Submission of Entries
                  </h4>
                  <p className="text-gray-500 mt-1">
                    Each school team is required to submit an entry to EIEU by
                    this date.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    2 &amp; 3 Dec 2021
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    Final Pitching
                  </h4>
                  <p className="text-gray-500 mt-1">
                    Each school team has delivered their final pitching at MMW
                    Library STEM Room.{" "}
                    <ScrollLink
                      to="results"
                      smooth={true}
                      duration={500}
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Click here
                    </ScrollLink>{" "}
                    for final pitching video and snapshots.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="adjudication" className="lg:py-20 py-10">
          <div className="container mx-auto px-4 lg:px-0">
            <div className="lg:flex">
              <div className="lg:flex-1 lg:pr-10">
                <h5 className="text-orange-600">#adjudication</h5>
                <h3 className="lg:text-3xl text-xl font-bold">Adjudication</h3>
                <p className="leading-loose mt-3">
                  The judging panel comprises 3 members from EdUHK staff and
                  external experts.
                </p>
              </div>
              <div className="lg:w-1/3">
                <img src={panelImage} width="500" height="300" alt="" />
              </div>
            </div>
          </div>
        </div>

        <div id="results" className="lg:py-20 py-10 bg-gray-50">
          <div className="container mx-auto px-4 lg:px-0">
            <div className="lg:flex">
              <div className="lg:flex-1 lg:pr-10">
                <h5 className="text-orange-600">#final_results</h5>
                <h3 className="lg:text-3xl text-xl font-bold">Final Results</h3>
                <p className="leading-loose mt-3">
                  We are pleased to announce the final results for the following
                  groups:
                </p>
                <div className="mt-4 mb-4">
                  <h5 className="font-bold text-lg">School Teams:</h5>
                  <table className="w-full bg-orange-50 table-auto my-3">
                    <thead>
                      <tr>
                        <th
                          width="50%"
                          className="border border-black px-2 py-2 text-left"
                        >
                          Awards
                        </th>
                        <th className="border border-black px-2 py-2 text-left">
                          Winning Teams
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-black px-2 py-2">
                          <b>Champion x 1:</b>
                          <ul className="list-disc ml-10">
                            <li>
                              $2,000 book coupon and an award certificate for
                              the EdUHK Student Team Leader
                            </li>
                            <li>
                              $2,000 book coupon and an award certificate for
                              the School Team
                            </li>
                          </ul>
                        </td>
                        <td className="border border-black px-2 py-2">
                          TWGHs Li Ka Shing College
                        </td>
                      </tr>
                      <tr>
                        <td className="border border-black px-2 py-2">
                          <b>Merit Prizes x 1:</b>
                          <ul className="list-disc ml-10">
                            <li>
                              $1,000 book coupon and an award certificate for
                              each EdUHK Student Team Leader
                            </li>
                            <li>
                              $1,000 book coupon and an award certificate for
                              each School Team
                            </li>
                          </ul>
                        </td>
                        <td className="border border-black px-2 py-2">
                          <ul className="list-disc ml-10">
                            <li>Christian Alliance Cheng Wing Gee College</li>
                            <li>Dr. Catherine F. Woo Memorial School</li>
                            <li>Ying Wa College</li>
                            <li>Tung Tak School</li>
                            <li>Chiu Yang Por Yen Primary School</li>
                          </ul>
                          <p className="mt-2 text-sm italic">
                            (listed in a random order)
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-8 mb-4">
                  <h5 className="font-bold text-lg">
                    Individual School Teams:
                  </h5>
                  <table className="w-full bg-orange-50 table-auto my-3">
                    <thead>
                      <tr>
                        <th
                          width="50%"
                          className="border border-black px-2 py-2 text-left"
                        >
                          Awards
                        </th>
                        <th className="border border-black px-2 py-2 text-left">
                          Winning Teams
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-black px-2 py-2">
                          <b>Champion:</b>
                          <ul className="list-disc ml-10">
                            <li>
                              $2,000 book coupon and an award certificate for
                              the Individual School Team
                            </li>
                          </ul>
                        </td>
                        <td className="border border-black px-2 py-2">
                          Rhenish Church Pang Hok Ko Memorial College
                        </td>
                      </tr>
                      <tr>
                        <td className="border border-black px-2 py-2">
                          <b>Special Prizes x 2:</b>
                          <ul className="list-disc ml-10">
                            <li>
                              $1,500 book coupon and an award certificate for
                              each Individual School Team
                            </li>
                          </ul>
                        </td>
                        <td className="border border-black px-2 py-2">
                          <ul className="list-disc ml-10">
                            <li>C.C.C. Heep Woh Primary School</li>
                            <li>CCC Ming Kei College</li>
                          </ul>
                          <p className="mt-2 text-sm italic">
                            (listed in a random order)
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-8 mb-4">
                  <h5 className="font-bold text-lg" id="video">
                    Video - Final Pitching:
                  </h5>

                  <ReactJWPlayer
                    playerId="player"
                    playerScript="https://cdn.jwplayer.com/libraries/l3BxhrLE.js"
                    file="https://stream.lib.eduhk.hk:1935/info/mp4:eieu-think-bts.mp4/playlist.m3u8"
                    image={withPrefix("/bts-snap.jpg")}
                    isAutoPlay={false}
                    className="w-3/4 mx-auto mt-5"
                  />
                </div>

                <div className="mt-8 mb-4">
                  <h5 className="font-bold text-lg">Snapshots:</h5>
                  <div className="mt-5 grid grid-cols-2 lg:grid-cols-8 gap-4">
                    {[...Array(totalSnapshots)].map((item, i) => (
                      <img
                        key={i}
                        src={withPrefix(
                          `/snapshots/thumbnails/pic${i + 1}.jpg`
                        )}
                        className="h-32 object-cover cursor-pointer inline-block"
                        alt=""
                        onClick={() => snapshotClicked(i)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-black py-10 px-10 md:py-20 md:px-20">
        <div className="container mx-auto">
          <div className="flex items-center justify-center">
            <div className="text-white">
              <p className="text-orange-400 mb-3 font-semibold text-center">
                Contact Us
              </p>
              <p className="text-center">EIEU, EdUHK Library</p>
              <p className="text-center">Phone: 29486511</p>
              <p className="text-center">
                Email:{" "}
                <a href="mailto:eieu@eduhk.hk" className="underline">
                  eieu@eduhk.hk
                </a>
              </p>
              <ul className="mt-6 list-style-none flex items-center justify-center">
                <li className="mr-6">
                  <a
                    href="https://www.eduhk.hk"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="opacity-50 hover:opacity-100"
                      src={eduhkBwLogo}
                      width="30"
                      height="20"
                      alt="EdUHK"
                    />
                  </a>
                </li>
                <li className="mr-6">
                  <a
                    href="https://www.lib.eduhk.hk"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="opacity-50 hover:opacity-100"
                      src={libBwLogo}
                      width="30"
                      height="20"
                      alt="EdUHK Library"
                    />
                  </a>
                </li>
                <li className="mr-6">
                  <a
                    href="https://eieu.lib.eduhk.hk"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="opacity-50 hover:opacity-100"
                      src={eieuBwLogo}
                      width="40"
                      height="20"
                      alt="EIEU"
                    />
                  </a>
                </li>
                <li className="mr-4">
                  <a
                    href="https://www.lttc.eduhk.hk"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="opacity-50 hover:opacity-100"
                      src={lttcBwLogo}
                      width="30"
                      height="20"
                      alt="LTTC"
                    />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.eduhk.hk/spfeo"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="opacity-50 hover:opacity-100"
                      src={spfeoBwLogo}
                      width="30"
                      height="20"
                      alt="SPFEO"
                    />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {lightboxOpen && (
        <Lightbox
          mainSrc={withPrefix(
            `/snapshots/originals/pic${snapshotIndex + 1}.jpg`
          )}
          nextSrc={
            snapshotIndex >= totalSnapshots - 1
              ? null
              : withPrefix(`/snapshots/originals/pic${snapshotIndex + 2}.jpg`)
          }
          prevSrc={
            snapshotIndex === 0
              ? null
              : withPrefix(`/snapshots/originals/pic${snapshotIndex - 1}.jpg`)
          }
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={() => setSnapshotIndex(snapshotIndex - 1)}
          onMoveNextRequest={() => setSnapshotIndex(snapshotIndex + 1)}
        />
      )}

      <div
        className={clsx(
          "fixed",
          "bottom-0",
          "right-0",
          "mr-5",
          "mb-10",
          "md:mr-20",
          "md:mb-20",
          !scrollVisible && "hidden"
        )}
      >
        <button
          onClick={() => scroll.scrollToTop({ duration: 500 })}
          className="w-10 h-10 md:w-12 md:h-12 bg-orange-500 shadow-lg rounded-full inline-block flex items-center justify-center focus:outline-none text-white"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            ></path>
          </svg>
        </button>
      </div>
    </div>
  );
}